import React, { useEffect, useState, useRef } from 'react';

const Preloader = ({ onLoadComplete }) => {
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    // Prevent body scroll during loading
    document.body.style.overflow = 'hidden';

    const loadAssets = async () => {
      const promises = [];
      let totalAssets = 0;
      let loadedAssets = 0;

      const updateProgress = () => {
        const newProgress = totalAssets > 0 ? Math.round((loadedAssets / totalAssets) * 100) : 0;
        setProgress(newProgress);
      };

      // Load hero video - most important asset
      const videoPromise = new Promise((resolve) => {
        let resolved = false;
        const video = document.createElement('video');
        video.src = '/videos/bg-chc_nwi3vn.mp4';
        video.preload = 'auto';
        video.muted = true;
        
        const handleSuccess = () => {
          if (!resolved) {
            resolved = true;
            loadedAssets++;
            updateProgress();
            resolve();
          }
        };

        const handleError = () => {
          if (!resolved) {
            resolved = true;
            loadedAssets++;
            updateProgress();
            resolve(); // Continue even if video fails
          }
        };

        // Try canplaythrough first (best), fallback to loadeddata
        if (video.readyState >= 3) {
          // Video already has enough data
          handleSuccess();
        } else {
          video.addEventListener('canplaythrough', handleSuccess, { once: true });
          video.addEventListener('loadeddata', handleSuccess, { once: true });
          video.addEventListener('error', handleError, { once: true });
          video.load();
        }
        
        videoRef.current = video;
      });
      promises.push(videoPromise);
      totalAssets++;

      // Wait for fonts to load
      if (document.fonts && document.fonts.ready) {
        const fontPromise = document.fonts.ready.then(() => {
          loadedAssets++;
          updateProgress();
        });
        promises.push(fontPromise);
        totalAssets++;
      }

      // Preload critical images
      const criticalImages = [
        '/logos/corevest-logo-green.png',
        '/logos/corvest-dark-logo.png',
        '/logos/corvest-app-icon.png'
      ];

      criticalImages.forEach((src) => {
        const imgPromise = new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => {
            loadedAssets++;
            updateProgress();
            resolve();
          };
          img.onerror = () => {
            loadedAssets++;
            updateProgress();
            resolve();
          };
        });
        promises.push(imgPromise);
        totalAssets++;
      });

      // Initial progress update
      updateProgress();

      // Minimum loading time for smooth animation (1.5 seconds)
      const minLoadTime = new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1500);
      });
      promises.push(minLoadTime);

      // Maximum loading time (timeout after 10 seconds)
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 10000);
      });

      // Wait for all promises or timeout
      await Promise.race([
        Promise.all(promises),
        timeoutPromise
      ]);

      // Final progress
      setProgress(100);

      // Small delay before hiding
      setTimeout(() => {
        setLoading(false);
        setTimeout(() => {
          // Re-enable body scroll
          document.body.style.overflow = '';
          onLoadComplete();
        }, 500);
      }, 300);
    };

    loadAssets();

    // Cleanup function
    return () => {
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loading && progress === 100) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 flex items-center justify-center transition-opacity duration-500 ${
        loading ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="text-center">
        {/* Logo */}
        <div className="mb-8 animate-pulse">
          <img
            src="/logos/corevest-logo-green.png"
            alt="COREVEST"
            className="h-20 w-auto mx-auto"
          />
        </div>

        {/* Spinner */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="absolute inset-0 border-4 border-gold-600/20 rounded-full"></div>
          <div
            className="absolute inset-0 border-4 border-transparent border-t-gold-500 rounded-full animate-spin"
            style={{
              animation: 'spin 1s linear infinite'
            }}
          ></div>
          <div
            className="absolute inset-0 border-4 border-transparent border-r-gold-400 rounded-full animate-spin"
            style={{
              animation: 'spin 1.5s linear infinite reverse'
            }}
          ></div>
        </div>

        {/* Progress Bar */}
        <div className="w-64 h-1 bg-gold-600/20 rounded-full overflow-hidden mx-auto mb-4">
          <div
            className="h-full bg-gradient-to-r from-gold-500 via-gold-400 to-gold-500 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Progress Text */}
        <p className="text-gold-400 text-sm font-medium">{progress}%</p>

        {/* Loading Text */}
        <p className="text-gray-300 text-sm mt-4 animate-pulse">Loading assets...</p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Preloader;

